import { useRef, useState } from 'react';
import useSWR from 'swr';
import classNames from 'classnames';
import { Typography } from '@johnlewispartnership/wtr-ingredients/dist/foundations/typography';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';

import recipesService, { defaultSummaryRequestBody } from 'services/recipes';
import type {
  RecipeFilterGroup,
  SummaryPreferences,
  SummaryResponse,
} from 'services/recipes/types';
import { useSearchParams } from 'renderer/hooks/useSearchParams';
import { useTrackVirtualPageView } from 'renderer/hooks/useTrackVirtualPageView';
import { queryToFilters } from 'renderer/utils/queryToFilters';

import {
  RECIPE_FEATURED_LAST_PUBLISHED,
  RECIPE_QUERY_PAGE,
  RECIPE_QUERY_SEARCH_TERM,
  RECIPE_QUERY_SORT_BY,
  RECIPE_SEARCH_SORT_OPTIONS,
} from '../constants';
import { mergeFilters } from '../utils/mergeFilters';
import { recipesSummaryCacheKey } from '../utils/recipesSummaryCacheKey';
import RecipeListingPageLayout from '../RecipeListingPageLayout';
import RecipeList from '../RecipeList';
import RecipeSortBy from '../RecipeSortBy';
import RecipeFilters from '../RecipeFilters';
import RecipePagination from '../RecipePagination';
import styles from './RecipeListingPage.module.scss';

type RecipeListingPageProps = {
  dietaryFilters: RecipeFilterGroup[];
  disableFilters: boolean;
  disableSortBy: boolean;
  gridSubtitle: string;
  gridTitle: string;
  gridType: 'recipe-cards' | 'featured';
  defaultSortBy: typeof RECIPE_FEATURED_LAST_PUBLISHED;
  preloadImages: boolean;
};

const fallbackData: SummaryResponse = { recipes: [], filters: [], totalMatches: 0 };

const getStatusMessage = ({
  error,
  hasNoFilteredResults,
  hasNoSearchResults,
  hasSearchResults,
  searchTerm,
  totalMatches,
}: {
  error: boolean;
  hasNoFilteredResults: boolean;
  hasNoSearchResults: boolean;
  hasSearchResults: boolean;
  searchTerm?: string;
  totalMatches?: number;
}) => {
  if (error) {
    return (
      <Typography styleAs="headingLarge">Sorry, something went wrong loading recipes</Typography>
    );
  }

  if (hasNoSearchResults) {
    return (
      <>
        <Typography styleAs="headingLarge">
          Sorry, we couldn't find anything for "{searchTerm}"
        </Typography>
        <Typography styleAs="paragraph">Try entering a different search term</Typography>
      </>
    );
  }

  if (hasNoFilteredResults) {
    return (
      <span className={styles.srOnly}>
        Sorry, we currently have no recipes matching your search. Try restarting your search.
      </span>
    );
  }

  if (hasSearchResults) {
    return (
      <span className={styles.srOnly}>
        {totalMatches} recipes found {searchTerm ? `for search term "${searchTerm}"` : ''}
      </span>
    );
  }

  return null;
};

const RecipeListingPage = ({
  gridType,
  disableFilters,
  disableSortBy,
  preloadImages,
}: RecipeListingPageProps) => {
  const { trackEvent } = useAnalytics();
  const trackVirtualPageView = useTrackVirtualPageView();
  const [searchParams] = useSearchParams();

  const searchTerm = searchParams.get(RECIPE_QUERY_SEARCH_TERM) ?? '';
  const hasInitialSearchTerm = useRef(!!searchTerm);
  const sortBy = searchParams.get(RECIPE_QUERY_SORT_BY) ?? '';
  const pageNumber = Number(searchParams.get(RECIPE_QUERY_PAGE) ?? 1) || 1;

  const [mergedFilters, setMergedFilters] = useState({
    searchTerm,
    filters: fallbackData.filters,
  });

  const [displayedRecipes, setDisplayedRecipes] = useState(fallbackData.recipes);

  const filtersParam = queryToFilters(searchParams);

  const fetchRecipesSummary = (): Promise<SummaryResponse> => {
    const hasRecipes = displayedRecipes.length;
    const size = defaultSummaryRequestBody.preferences.size;

    const preferences: SummaryPreferences = {
      ...defaultSummaryRequestBody.preferences,
      start: hasRecipes ? (pageNumber - 1) * size : 0,
      size: hasRecipes ? size : size * pageNumber,
      ...(sortBy && { sortBy }),
    };

    return recipesService.postSummary({
      body: {
        ...(searchTerm && { search: { term: searchTerm, tags: [[]] } }),
        ...(filtersParam.length && { filters: filtersParam }),
        preferences,
      },
    });
  };

  const { data, isLoading, isValidating, error } = useSWR(
    recipesSummaryCacheKey(searchTerm, sortBy, pageNumber, filtersParam),
    fetchRecipesSummary,
    {
      keepPreviousData: true,
      fallbackData,
      onSuccess: response => {
        // TODO(WPIP-81427,WPIP-81746,WPIP-81747): further analytics tickets should abstract this tracking logic.
        const sortType = RECIPE_SEARCH_SORT_OPTIONS.find(
          ({ value }) => value === sortBy,
        )?.label.toLowerCase();

        if (hasInitialSearchTerm.current) {
          trackEvent({
            event: 'recipe_search',
            number_of_results: response.totalMatches,
            search_term: searchTerm,
            search_term_type: 'url',
            sort_type: sortType,
          });

          hasInitialSearchTerm.current = false;
        }

        const isNewSearchTerm = mergedFilters.searchTerm !== searchTerm;

        if ((!mergedFilters.searchTerm && searchTerm) || isNewSearchTerm) {
          trackEvent({
            event: 'recipe_search',
            number_of_results: response.totalMatches,
            search_term: searchTerm,
            search_term_type: 'typed',
            sort_type: sortType,
          });

          trackVirtualPageView();
        }

        setMergedFilters(prev => ({
          searchTerm,
          filters:
            prev.searchTerm !== searchTerm
              ? response.filters
              : mergeFilters(prev.filters, response.filters),
        }));

        setDisplayedRecipes(prevRecipes => {
          if (pageNumber === 1) {
            return response.recipes;
          }
          const existingIds = new Set(prevRecipes.map(recipe => recipe.id));
          const newRecipes = response.recipes.filter(recipe => !existingIds.has(recipe.id));
          return [...prevRecipes, ...newRecipes];
        });
      },
    },
  );

  const { filters } = mergedFilters;
  const { totalMatches } = data;

  const showPagination = totalMatches > displayedRecipes.length;

  const hasNoFilteredResults = !isLoading && totalMatches === 0 && filtersParam.length > 0;
  const hasNoSearchResults =
    !isLoading && !isValidating && !hasNoFilteredResults && !!searchTerm && totalMatches === 0;
  const hasSearchResults = !isLoading && !isValidating && !!totalMatches;
  const hasPageLevelError = hasNoSearchResults || !!error;
  const shouldShowLayout = (!isLoading || displayedRecipes.length > 0) && !hasPageLevelError;

  const statusComponent = getStatusMessage({
    error: !!error,
    hasNoSearchResults,
    hasNoFilteredResults,
    hasSearchResults,
    searchTerm,
    totalMatches,
  });

  return (
    <>
      <div
        aria-live="polite"
        aria-atomic="true"
        role="status"
        data-testid="recipe-results-status"
        className={classNames({ [styles.statusContainer]: hasPageLevelError })}
      >
        {statusComponent}
      </div>
      {shouldShowLayout && (
        <RecipeListingPageLayout
          gridType={gridType}
          disableFilters={disableFilters}
          disableSortBy={disableSortBy}
          preloadImages={preloadImages}
          filterComponent={<RecipeFilters filters={filters} />}
          sortByComponent={!hasNoFilteredResults && <RecipeSortBy />}
          listingComponent={
            <RecipeList recipes={displayedRecipes} hasNoFilteredResults={hasNoFilteredResults} />
          }
          paginationComponent={
            showPagination ? (
              <RecipePagination isLoading={isValidating} currentPage={pageNumber} />
            ) : null
          }
        />
      )}
    </>
  );
};

export default RecipeListingPage;
